<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">Drone Reports</h1>
        <p class="text-muted">Manage drone reports across all organizations</p>
      </div>
      <div class="col-md-6 text-md-end">
        <button type="button" class="btn btn-outline-secondary" (click)="loadReports()">
          <i class="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- Generation Section -->
    <div class="card mb-4">
      <div class="card-header batch-header cursor-pointer user-select-none" (click)="toggleGenerateSection()">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="fw-semibold mb-0">
            <i class="bi bi-collection me-2"></i>
            Generate Drone Report
          </h6>
          <div class="d-flex align-items-center gap-3">
            <span *ngIf="selectedDrone != null" class="badge bg-primary">
              {{ selectedDrone.name }} selected
            </span>
                <i class="bi text-body-secondary" [class.bi-chevron-down]="!generateSectionExpanded" [class.bi-chevron-up]="generateSectionExpanded"></i>
          </div>
        </div>
      </div>
      <div class="card-body" *ngIf="generateSectionExpanded">
        <div class="row">
          <!-- Drone Selection Column -->
          <div class="col-lg-6 mb-4 mb-lg-0">
            <h6 class="small fw-semibold text-uppercase mb-3">Select Drone</h6>

            <!-- Drone Search -->
            <div class="input-group mb-3">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search drones..."
                [(ngModel)]="droneSearch"
                (input)="applyDroneFilter()"
              />
            </div>

            <!-- Drone List -->
            <div class="batch-org-list overflow-y-auto border rounded">
                <table class="table table-sm table-hover mb-0">
                  <thead class="sticky-top">
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>Drone</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="filteredDrones.length === 0">
                      <td colspan="2" class="text-center text-muted py-3">
                        No drones found
                      </td>
                    </tr>
                    <tr
                      *ngFor="let drone of filteredDrones"
                      (click)="selectedDrone = selectedDrone?.id === drone.id ? null : drone"
                      class="cursor-pointer"
                      [class.table-active]="isDroneSelected(drone.id)"
                    >
                      <td class="text-center py-2">
                        <input
                          type="radio"
                          name="generateDrone"
                          class="form-check-input cursor-pointer"
                          [checked]="isDroneSelected(drone.id)"
                          (click)="$event.stopPropagation(); selectedDrone = drone"
                        />
                      </td>
                      <td class="py-2">
                        <span class="fw-medium" [class.text-primary]="isDroneSelected(drone.id)">{{ drone.name }}</span>
                        <small class="text-muted d-block">{{ drone.id }}</small>
                      </td>
                    </tr>
                  </tbody>
                </table>
            </div>
          </div>

          <!-- Options Column -->
          <div class="col-lg-6">
            <!-- Date Range -->
            <div class="row g-3 mb-4">
              <div class="col-6">
                <label class="form-label small text-body-secondary">Start Date</label>
                <input
                  type="date"
                  class="form-control"
                  [(ngModel)]="reportStartDate"
                  [class.is-invalid]="reportStartDate && reportEndDate && !isDateRangeValid()"
                />
              </div>
              <div class="col-6">
                <label class="form-label small text-body-secondary">End Date</label>
                <input
                  type="date"
                  class="form-control"
                  [(ngModel)]="reportEndDate"
                  [class.is-invalid]="reportStartDate && reportEndDate && !isDateRangeValid()"
                />
              </div>
              <div class="col-12" *ngIf="reportStartDate && reportEndDate && !isDateRangeValid()">
                <div class="invalid-feedback d-block">
                  <i class="bi bi-exclamation-circle me-1"></i>
                  End date must be on or after start date
                </div>
              </div>
            </div>


            <!-- Generate Button -->
            <div class="d-grid">
              <button
                type="button"
                class="btn btn-primary py-2 fw-medium"
                (click)="confirmDroneReportGeneration()"
                [disabled]="!canSubmitReport() || reportGenerating"
              >
                <span *ngIf="reportGenerating" class="spinner-border spinner-border-sm me-2"></span>
                <i *ngIf="!reportGenerating" class="bi bi-file-earmark-plus me-2"></i>
                Generate {{ selectedDrone?.name }} Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Search -->
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by report name..."
                (keyup.enter)="onSearch()"
                [(ngModel)]="searchQuery"   
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Drone Filter -->
          <div class="col-md">
            <label class="form-label small text-body-secondary">Drone</label>
            <select class="form-select" (change)="onFilterChange()" [(ngModel)]="filterDrone">
              <option value="">All Drones</option>
              <option *ngFor="let drone of drones" [value]="drone.id">{{ drone.name }}</option>
            </select>
          </div>

          <!-- Organization Filter -->
          <div class="col-md">
            <label class="form-label small text-body-secondary">Organization</label>
            <select class="form-select" (change)="onFilterChange()" [(ngModel)]="filterOrganization">
              <option value="">All Organizations</option>
              <option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</option>
            </select>
          </div>

          <!-- Status Filter -->
          <div class="col-md">
            <label class="form-label small text-body-secondary">Status</label>
            <select class="form-select" (change)="onFilterChange()" [(ngModel)]="filterStatus">
              <option *ngFor="let status of statusOptions" [value]="status.value">{{ status.label }}</option>
            </select>
          </div>

          <!-- Date Range -->
          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">Created From</label>
            <input
              type="date"
              class="form-control"
              (change)="onFilterChange()"
              [(ngModel)]="filterStartDate"
            />
          </div>

          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">Created To</label>
            <input
              type="date"
              class="form-control"
              (change)="onFilterChange()"
              [(ngModel)]="filterEndDate"
            />
          </div>
        </div>

         <!-- Clear Filters Button -->
        <div class="row mt-3" *ngIf="searchQuery || filterDrone||filterOrganization || filterStatus !== 'all' || filterStartDate || filterEndDate">
          <div class="col-12">
            <button type="button" class="btn btn-outline-primary" (click)="clearFilters()">
              <i class="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <!-- Reports Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
            <thead>
                <tr>
                    <th>Report Name</th>
                    <th>Drone</th>
                    <th>Organization</th>
                    <th>Date Range</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th class="text-end">Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngIf="reports.length === 0">
                    <td colspan="7" class="text-center text-muted py-4">
                        No reports found
                    </td>
                </tr>
                <tr *ngFor="let report of reports">
                    <td>
                        <div class="d-flex align-items-center gap-3">
                            <div class="entity-avatar entity-avatar-square">
                                <i class="bi bi-file-earmark-text"></i>
                            </div>
                            <div>
                                <div class="fw-medium">{{ report.name }}</div>
                                <small class="text-muted">{{ report.scope === 'full_history' ? 'Full History' : 'Single Incident'}} {{ report.generatedByAdmin ? '• Admin Generated' : '' }} </small>
                            </div>
                        </div>
                    </td>
                    <td>
                        <span>{{ getDroneName(report.droneId) }}</span>
                    </td>
                     <td>
                        <span>{{ getOrganizationName(report.organizationId) }}</span>
                    </td>
                    <td>
                        <small class="text-muted">{{ report.scope === 'full_history' ? formatDateRange(report.startDate, report.endDate) : formatDate(report.startDate)}}</small>
                    </td>
                    <td>
                        <span class="badge" [ngClass]="getStatusClass(report.status)">
                            <i class="bi" [class]="getStatusIcon(report.status)"></i>
                            {{ getStatusLabel(report.status) }}
                        </span>
                        <div *ngIf="report.status === 'generating'" class="progress mt-1" style="height: 4px;">
                            <div class="progress-bar progress-bar-striped progress-bar-animated"
                                 [style.width.%]="report.generationProgress">
                            </div>
                        </div>
                    </td>
                    <td>
                        <small class="text-muted">{{ formatDate(report.createdAt)  }}</small>
                    </td>
                    <td class="text-end text-nowrap">
                        <!-- View Button -->
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-primary me-1"
                            (click)="viewReport(report)"
                            [disabled]="report.status === 'generating'"
                            [title]="report.status === 'generating' ? 'Available when generation finishes' : 'View Details'"
                            >
                            <i class="bi bi-eye"></i>
                        </button>

                         <!-- Retry Button (only for failed) -->
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-warning me-1"
                            title="Retry Failed Job"
                            *ngIf="canRetry(report)"  
                            [disabled]="actionLoading"  
                            (click)="retryReport(report)"
                            >
                            <i class="bi bi-arrow-repeat"></i>
                        </button>

                        <!-- Regenerate Button -->
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-info me-1"
                            (click)="confirmRegenerate(report)"
                            [disabled]="report.status === 'generating' || actionLoading"
                            title="Regenerate Report"
                            >
                            <i class="bi bi-arrow-clockwise"></i>
                        </button>

                        <!-- Delete Button -->
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            (click)="confirmDelete(report)"
                            [disabled]="!canDelete(report) || actionLoading"
                            [title]="canDelete(report) ? 'Delete Report' : 'Cannot delete while generating'"
                            >
                            <i class="bi bi-trash"></i>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
      </div>
      <!-- Pagination -->
      <div class="card-footer" *ngIf="totalPages > 1">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalReports) }} of {{ totalReports }} reports
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div> 


<!-- Drone Report Generation Confirmation Modal -->
<div class="modal fade" id="droneReportConfirmModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-collection text-primary me-2"></i>
          Confirm Drone Report Generation
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelDroneReportConfirmation()"></button>
      </div>
      <div class="modal-body">
        <p>{{ getDroneReportConfirmationText() }}</p>
        <div class="card mb-3">
          <div class="card-body py-3">
            <div class="row">
              <div class="col-6">
                <small class="text-muted d-block">Drone</small>
                <strong>{{ selectedDrone?.name }}</strong>
              </div>
              <div class="col-6">
                <small class="text-muted d-block">Date Range</small>
                <strong>{{ formatDateRange(reportStartDate, reportEndDate) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelDroneReportConfirmation()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="executeReportGeneration()"
          [disabled]="reportGenerating"
        >
          <span *ngIf="reportGenerating" class="spinner-border spinner-border-sm me-1"></span>
          Generate Report
        </button>
      </div>
    </div>
  </div>
</div>


<!-- Report Detail Modal -->
<div class="modal fade" id="detailModal" tabindex="-1">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-file-earmark-text me-2"></i>Report Details
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="closeDetailModal()"></button>
      </div>
      <div class="modal-body" *ngIf="selectedReport">
        <!-- Report Metadata -->
        <div class="row mb-4">
          <div class="col-md-6">
            <h6 class="text-muted mb-2">Report Information</h6>
            <dl class="row mb-2">
              <dt class="col-5 fw-normal text-body-secondary">Name</dt><dd class="col-7 fw-medium">{{ selectedReport.name }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Drone</dt><dd class="col-7 fw-medium">{{ getDroneName(selectedReport.droneId) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Organization</dt><dd class="col-7">{{getOrganizationName(selectedReport.organizationId) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Report Scope</dt><dd class="col-7">{{ selectedReport.scope === 'full_history' ? 'Full History' : 'Single Incident'}}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Date Range</dt><dd class="col-7">{{ formatDateRange(selectedReport.startDate, selectedReport.endDate) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Created</dt><dd class="col-7">{{ formatDate(selectedReport.createdAt) }}</dd>
            </dl>
          </div>
          <div class="col-md-6">
            <h6 class="text-muted mb-2">Status</h6>
            <div class="mb-3">
              <span class="badge" [ngClass]="getStatusClass(selectedReport.status)">
                <i class="bi" [class]="getStatusIcon(selectedReport.status)"></i>
                {{ getStatusLabel(selectedReport.status) }}
              </span>
            </div>

            <!-- Progress bar for generating -->
            <div *ngIf="selectedReport.status === 'generating'" class="mb-3">
              <label class="form-label small text-muted">Generation Progress</label>
              <div class="progress" style="height: 20px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated bg-info"
                     [style.width.%]="selectedReport.generationProgress">
                  {{ selectedReport.generationProgress }}%
                </div>
              </div>
            </div>

            <!-- Report stats for completed -->
            <div *ngIf="selectedReport.status === 'ready' || selectedReport.status === 'submitted'">
              <dl class="row mb-2">
                <ng-container *ngIf="selectedReport.recordCount !== null"><dt class="col-5 fw-normal text-body-secondary">Records</dt><dd class="col-7">{{ selectedReport.recordCount }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.fileSize"><dt class="col-5 fw-normal text-body-secondary">File Size</dt><dd class="col-7">{{ selectedReport.fileSize }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.submittedAt"><dt class="col-5 fw-normal text-body-secondary">Submitted</dt><dd class="col-7">{{ formatDate(selectedReport.submittedAt) }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.submittedTo"><dt class="col-5 fw-normal text-body-secondary">Submitted To</dt><dd class="col-7">{{ selectedReport.submittedTo }}</dd></ng-container>
              </dl>
            </div>

            <!-- Error message for failed -->
            <div *ngIf="selectedReport.status === 'failed' && selectedReport.errorMessage" class="alert alert-danger">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Error:</strong> {{ selectedReport.errorMessage }}
            </div>
          </div>
        </div>

        <!-- Job Information -->
        <div *ngIf="selectedReport.jobs && selectedReport.jobs.length > 0">
          <h6 class="text-muted mb-2">Job History</h6>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Created</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let job of selectedReport.jobs">
                  <td><code class="small bg-body-tertiary rounded px-1">{{ job.id }}</code></td>
                  <td>
                    <span class="badge bg-secondary">{{ job.status }}</span>
                  </td>
                  <td>{{ job.attemptsMade }}</td>
                  <td>{{ formatTimestamp(job?.createdAt ?? job?.timestamp) }}</td>
                  <td>{{ formatTimestamp(job?.finishedAt ?? job?.finishedOn) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div *ngIf="selectedReport.jobs[0]?.failedReason" class="alert alert-danger mt-2">
            <strong>Failure Reason:</strong> {{ selectedReport.jobs[0].failedReason }}
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button
          *ngIf="selectedReport?.reportUrl"
          type="button"
          class="btn btn-primary"
          (click)="openReportUrl()"
        >
          <i class="bi bi-download me-2"></i>
          Download Report
        </button>
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="closeDetailModal()">
          Close
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Modal -->
<div class="modal fade" id="deleteModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-exclamation-triangle text-danger me-2"></i>
          Confirm Delete
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelDelete()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToDelete">
        <p>Are you sure you want to delete the following report?</p>
        <div class="card">
          <div class="card-body py-2">
            <strong>{{ reportToDelete.name }}</strong>
            <br>
            <small class="text-muted">{{ getDroneName(reportToDelete.droneId) }}</small>
          </div>
        </div>
        <div class="alert alert-warning mt-3 mb-0">
          <i class="bi bi-info-circle me-2"></i>
          This action cannot be undone. The report will be permanently deleted.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelDelete()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="executeDelete()"
          [disabled]="actionLoading"
        >
          <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
          Delete Report
        </button>
      </div>
    </div>
  </div>
</div>


<!-- Regenerate Confirmation Modal -->
<div class="modal fade" id="regenerateModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-arrow-clockwise text-info me-2"></i>
          Confirm Regenerate
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelRegenerate()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToRegenerate">
        <p>Are you sure you want to regenerate the following report?</p>
        <div class="card">
          <div class="card-body py-2">
            <strong>{{ reportToRegenerate.name }}</strong>
            <br>
            <small class="text-muted">{{ getOrganizationName(reportToRegenerate.organizationId) }}</small>
          </div>
        </div>
        <div class="alert alert-info mt-3 mb-0">
          <i class="bi bi-info-circle me-2"></i>
          A new generation job will be created. This will overwrite any existing report file.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelRegenerate()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-info"
          (click)="executeRegenerate()"
          [disabled]="actionLoading"
        >
          <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
          Regenerate Report
        </button>
      </div>
    </div>
  </div>
</div>
